// Setup steps and the opt in notes: every fixed member word this feature adds, in one place.
// Reviewed whole on the staff review page (scripts/setup-copy-review.mjs). Change a word and the review id changes.
// Rules: grade 5 at most, sentences of 15 words or fewer, no dashes, no semicolons, nothing that pressures, no health claims.

export const GOAL_MAX = 3; // also exported from member-goals.mjs: the two must agree (tests/setup-copy.test.ts holds them equal)

export const SETUP_COPY = Object.freeze({
  // goals
  goalsHelp: 'Pick up to three. We start with the basics and build your path around them.',
  goalsFull: 'Three is the most. Tap one to swap it.',
  // the sounds step (FEEL on only)
  soundsEye: 'Sounds',
  soundsTitle: 'Would you like soft sounds?',
  soundsLines: Object.freeze(['Quests can play soft water, wind and birds.', 'Your phone can give a small tap.', 'They are quiet and short.', 'You can change this any time in Profile.']),
  soundsIphone: 'On iPhone, the side switch mutes the sounds.',
  soundsSample: 'Hear a wave',
  soundsYes: 'Yes, soft sounds',
  soundsNo: 'No sounds for now',
  startSoundOn: 'Sounds on',
  startSoundOff: 'Sounds off',
  // consents
  required: 'Required',
  optional: 'Optional',
  consents: Object.freeze([
    Object.freeze({ key: 'education', tag: 'required', title: 'I understand this is wellness education', body: 'Marie and the lessons guide and educate. They do not diagnose or treat. I will keep seeing my healthcare provider for medical needs.' }),
    Object.freeze({ key: 'logAccess', tag: 'required', title: 'Marie may read my log to guide me', body: 'Your sleep, meals, mood and reactions help Marie notice patterns. Only you and your practitioner see them.' }),
    Object.freeze({ key: 'reminders', tag: 'optional', title: 'Send me gentle reminders', body: 'A short nudge for your lesson and daily log. Change it any time.' }),
  ]),
  termsBody: 'They explain what Whole Health Harmony is, and what it is not.',
  disclaimerLabel: 'Our disclaimer',
  // the plan ready screen
  skippedTitle: 'Things you skipped',
  skippedSub: 'No rush. Each one can wait.',
  // Profile
  thingsTitle: 'Things you can turn on',
  thingsSub: 'You said not now to these. Each one is a tap away.',
  turnOn: 'Turn on now',
  later: 'Maybe later',
  remindersOn: 'Reminders are on.',
  goalsChange: 'Change my goals',
  goalsSave: 'Save my goals',
  goalsSaved: 'Your goals are saved.',
  logOn: 'Marie and your practitioner use it to notice patterns.',
  logOff: 'Marie cannot see your log now. Your practitioner cannot see it either. A severe reaction you log still reaches your practitioner for safety.',
  // take it with you (the finish screen and #/get-app)
  takeTitle: 'Take it with you',
  takePhone: 'Add Harmony to your Home Screen. It opens full screen and keeps your reminders.',
  takeDesk: 'Harmony works best on your phone. Open it there and add it to your Home Screen.',
  qrHelp: 'Point your phone camera at this code.',
  sendLink: 'Send me a sign in link',
  sendLinkHelp: 'We will email you a link. Open it on your phone, then add the app.',
  sendLinkSent: 'Sent. Check your email on your phone. The link works once and ends in 15 minutes.',
  osHint: 'On your phone, sign in the same way you did here.',
  installHere: 'Install on this computer',
  installHereHelp: 'Your browser can add Harmony as an app here too.',
  sameApp: 'It is this same web app, saved to your phone. No app store, nothing to buy.',
  iconLine: 'This is the icon you will see.',
  iconAlt: 'The Harmony app icon',
  onThisPhone: 'On this phone',
  onYourPhone: 'On your phone',
  onThisComputer: 'On this computer',
});

/** Every opt in that gets a "what you will miss" note. `miss` is what she loses, `later` is the way back. */
export const OPT_INS = Object.freeze([
  Object.freeze({ id: 'sound', label: 'Soft sounds and small taps', miss: 'You will miss the soft water, birds and small taps.', later: 'You can turn them on any time in Profile.' }),
  Object.freeze({ id: 'nature', label: 'Nature sounds', miss: 'You will miss the birds, the rain and the soft fire.', later: 'You can turn them on any time in Profile.' }),
  Object.freeze({ id: 'night', label: 'Night sounds when you pause', miss: 'You will miss the soft crickets when you stop to think.', later: 'You can turn them on any time in Profile.' }),
  Object.freeze({ id: 'reminders', label: 'Gentle reminders', miss: 'Without them, Marie cannot nudge you about a lesson or your log.', later: 'You can turn them on any time in Profile.' }),
  Object.freeze({ id: 'push', label: 'Phone notifications', miss: 'Without them, nudges only show inside the app.', later: 'You can turn them on any time in Profile.' }),
  Object.freeze({ id: 'logAccess', label: 'Marie may read my log', miss: 'Marie cannot notice patterns in your log. Your practitioner cannot see it either.', later: 'You can turn this on any time in Profile.' }),
  Object.freeze({ id: 'intake', label: 'What brings you here', miss: 'Without your answers, we cannot point out lessons for you.', later: 'You can answer any time in Profile.', cta: 'Answer now' }),
  Object.freeze({ id: 'scanFiles', label: 'Your own scan files', miss: 'Without this, you cannot add your own scan files.', later: 'You can agree any time from your scan files page.', cta: 'Open my scan files' }),
  Object.freeze({ id: 'app', label: 'The app on your phone', miss: 'Without the app, lessons open in your browser, not full screen. On iPhone, reminders need the app.', later: 'You can add it any time from Profile.', cta: 'Get the app' }),
]);
export const optInById = (id) => OPT_INS.find((o) => o.id === id);
/** The one line she reads when she declines: what she misses, then the way back. */
export const missLine = (id) => { const o = optInById(id); return `${o.miss} ${o.later}`; };
